import { AiScoringConstants } from '../aiScoringConstants';
import { AttributeItem } from '../AiAttributesList/aiAttributeList.types';

export interface ProcessedAttribute extends AttributeItem {
  id: number;
  is_valid: boolean;
  reason: string;
  valid_type: 'success' | 'warning' | 'error';
  isNewAttribute: boolean;
}

/**
 * Centralized validation logic for attributes
 * Handles both API responses and user-created attributes consistently
 */
export const validateAttribute = (attr: AttributeItem): ProcessedAttribute => {
  // Ensure we have an ID
  const id = typeof attr.id === 'number' ? attr.id : getNextTempId();

  // Check minimum length requirement
  const isValidLength =
    attr.description.trim().length >= AiScoringConstants.MIN_ATTRIBUTE_LENGTH;

  // Determine if this is from API response vs user-created
  // Use the explicit isNewAttribute flag for new attributes
  // Also treat attributes with id: null and attribute_type: null as new (unsaved in DB)
  const isUnsavedApiAttribute =
    attr.id === null && attr.attribute_type === null;
  const isUserCreatedAttribute = attr.isNewAttribute || isUnsavedApiAttribute;

  // Validation logic:
  // 1. If API explicitly provided is_valid, use it
  // 2. If user-created attribute, use local validation
  // 3. If API response has reason/valid_type but no is_valid, interpret based on valid_type
  // 4. If API response without any validation info, default to false (validation pending)
  let is_valid: boolean;
  let reason: string;
  let valid_type: 'success' | 'warning' | 'error';

  if (attr.is_valid !== undefined && attr.is_valid !== null) {
    // API explicitly provided is_valid
    is_valid = attr.is_valid;
    reason = attr.reason || '';
    valid_type = attr.valid_type || 'success';
  } else if (attr.reason || attr.valid_type) {
    // API response with validation info but no explicit is_valid
    // This includes unsaved API attributes with validation errors/warnings
    // If valid_type is 'warning' or 'error', treat as invalid
    // If valid_type is 'success', treat as valid
    const apiValidType = attr.valid_type || 'warning';
    is_valid = apiValidType === 'success';
    reason = attr.reason || '';
    valid_type = apiValidType;
  } else if (isUserCreatedAttribute) {
    // User-created attribute validation
    // Only new empty attributes should remain neutral, everything else gets validated
    const isEmpty = attr.description.trim().length === 0;

    if (isEmpty && attr.isNewAttribute) {
      // Only new empty attributes remain neutral
      is_valid = true;
      reason = '';
      valid_type = 'success';
    } else {
      // All other user-created attributes get normal validation
      is_valid = isValidLength;
      reason = isValidLength
        ? ''
        : `Attribute must be at least ${AiScoringConstants.MIN_ATTRIBUTE_LENGTH} characters long`;
      valid_type = isValidLength ? 'success' : 'error';
    }
  } else {
    // Fallback for unknown cases - default to valid state
    is_valid = true;
    reason = '';
    valid_type = 'success';
  }

  // Override if API says valid but fails local validation
  if (attr.is_valid === true && !isValidLength) {
    is_valid = false;
    reason = `Attribute must be at least ${AiScoringConstants.MIN_ATTRIBUTE_LENGTH} characters long`;
    valid_type = 'error';
  }

  const result = {
    ...attr,
    id,
    is_valid,
    reason,
    valid_type,
    isNewAttribute: attr.isNewAttribute || isUnsavedApiAttribute,
  };

  return result;
};

/**
 * Process an array of attributes with validation
 */
export const processAttributes = (
  attributes: AttributeItem[]
): ProcessedAttribute[] => {
  if (!Array.isArray(attributes)) {
    return [];
  }

  const result = attributes.map(validateAttribute);

  return result;
};

/**
 * Check if the overall attribute list is valid
 */
export const isAttributeListValid = (
  attributes: ProcessedAttribute[],
  interactedAttributes: Set<number> = new Set()
): boolean => {
  if (attributes.length === 0) return false;

  // Filter out empty new attributes that haven't been interacted with
  const relevantAttributes = attributes.filter((attr) => {
    const hasBeenInteracted = interactedAttributes.has(attr.id);
    const isEmpty = attr.description.trim().length === 0;

    // Ignore new attributes that are empty and haven't been interacted with
    if (attr.isNewAttribute && isEmpty && !hasBeenInteracted) {
      return false;
    }

    return true;
  });

  // Check minimum required attributes
  const attributesWithDescription = relevantAttributes.filter(
    (attr) => attr.description.trim().length > 0
  );

  if (
    attributesWithDescription.length <
    AiScoringConstants.MIN_ATTRIBUTES_REQUIRED
  ) {
    return false;
  }

  // Check if any attribute is explicitly invalid
  return !relevantAttributes.some(
    (attr) => attr.is_valid === false && attr.valid_type === 'error'
  );
};

/**
 * Generate next temporary ID for new attributes
 */
function getNextTempId(): number {
  if (typeof getNextTempId.counter !== 'number') getNextTempId.counter = -1;
  return getNextTempId.counter--;
}
// @ts-expect-error legacy untyped AI-scoring payload
getNextTempId.counter = -1;

/**
 * Create a new attribute with neutral validation state
 * The UX for showing validation errors is handled in the hook
 */
export const createNewAttribute = (position: number): ProcessedAttribute => {
  return {
    id: getNextTempId(),
    description: '',
    position,
    isNewAttribute: true,
    // New attributes start with neutral state - no errors shown until user interaction
    is_valid: true,
    reason: '',
    valid_type: 'success',
    // Optional fields with defaults
    attribute_type: null,
  };
};
